import React, { useEffect, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { reApi, errMsg, fmtDateTime, pretty } from "@/lib/residentApi";
import { inputCls, GoldButton, GhostButton, Pill, Empty, ErrorLine, EditToggle } from "./ui";

const TONE = { pass: "green", confirmed: "green", attended: "green", good: "green", fail: "red", missed: "red", concern: "red", canceled: "dim", expired: "dim" };

export default function CheckInsTab({ resident }) {
  const [data, setData] = useState({ check_ins: null, types: {} });
  const [type, setType] = useState("");
  const [form, setForm] = useState({ type: "wellness", status: "good", notes: "" });
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState(null);
  const saveEdit = async () => {
    setError(null);
    try { await reApi.patch(`/check-ins/${edit.id}`, { status: edit.status, notes: edit.notes }); setEdit(null); load(); } catch (e) { setError(errMsg(e)); }
  };

  const load = () => reApi.get("/check-ins", { resident_id: resident.id }).then(setData).catch((e) => setError(errMsg(e)));
  useEffect(() => { load(); }, [resident.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    setError(null);
    try { await reApi.post("/check-ins/submit", { resident_id: resident.id, ...form }); setForm((f) => ({ ...f, notes: "" })); load(); }
    catch (e) { setError(errMsg(e)); }
  };
  const remove = async (id) => { await reApi.del(`/check-ins/${id}`); load(); };
  const rows = (data.check_ins || []).filter((c) => !type || c.type === type);
  const types = Object.keys(data.types);

  return (
    <div data-testid="checkins-tab" className="space-y-4">
      <EditToggle editing={editing} setEditing={(v) => { setEditing(v); setEdit(null); }} label="Edit check-ins" testId="checkins" />
      <div className="p-3 border border-white/10 rounded-sm space-y-2">
        <div className="grid sm:grid-cols-3 gap-2">
          <select data-testid="checkin-input-type" value={form.type} className={inputCls}
            onChange={(e) => setForm({ ...form, type: e.target.value, status: data.types[e.target.value]?.[0] || "" })}>
            {types.map((t) => <option key={t} value={t}>{pretty(t)}</option>)}
          </select>
          <select data-testid="checkin-input-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls}>
            {(data.types[form.type] || []).map((s) => <option key={s} value={s}>{pretty(s)}</option>)}
          </select>
          <GoldButton data-testid="checkin-submit" onClick={submit} className="justify-center"><Plus className="h-3 w-3" /> Record check-in</GoldButton>
        </div>
        <input data-testid="checkin-input-notes" value={form.notes} placeholder="Notes (optional)" onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls} />
        <ErrorLine testId="checkin-error">{error}</ErrorLine>
      </div>
      <div className="flex flex-wrap gap-2">
        {["", ...types].map((t) => (
          <button key={t || "all"} type="button" onClick={() => setType(t)} data-testid={`checkins-filter-${t || "all"}`}
            className={`px-3 py-1 rounded-full border font-mono text-[10px] tracking-[0.18em] uppercase ${type === t ? "border-nex-gold bg-nex-gold/15 text-nex-gold" : "border-white/10 text-nex-muted"}`}>
            {t ? pretty(t) : "All"} · {(data.check_ins || []).filter((c) => !t || c.type === t).length}
          </button>
        ))}
      </div>
      {data.check_ins && !rows.length ? <Empty testId="checkins-empty">No check-ins yet.</Empty> : (
        <ul className="divide-y divide-white/5 border border-white/5 rounded-sm">
          {rows.map((c) => (edit?.id === c.id ? (
            <li key={c.id} data-testid={`checkin-edit-${c.id}`} className="px-3 py-2 flex flex-wrap items-center gap-2 text-sm bg-nex-gold/5">
              <Pill tone="dim">{pretty(c.type)}</Pill>
              <select data-testid={`checkin-edit-status-${c.id}`} value={edit.status} onChange={(e) => setEdit({ ...edit, status: e.target.value })} className={`${inputCls} !w-auto`}>
                {(data.types[c.type] || [c.status]).map((s) => <option key={s} value={s}>{pretty(s)}</option>)}
              </select>
              <input data-testid={`checkin-edit-notes-${c.id}`} value={edit.notes} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} placeholder="Notes" className={`${inputCls} basis-full sm:basis-auto sm:flex-1 sm:min-w-[10rem]`} />
              <GhostButton data-testid={`checkin-edit-cancel-${c.id}`} onClick={() => setEdit(null)}>Cancel</GhostButton>
              <GoldButton data-testid={`checkin-edit-save-${c.id}`} onClick={saveEdit}>Save</GoldButton>
            </li>
          ) : (
            <li key={c.id} data-testid={`checkin-${c.id}`} className="px-3 py-2 flex flex-wrap sm:flex-nowrap items-center gap-2 sm:gap-3 text-sm min-w-0">
              <Pill tone="dim">{pretty(c.type)}</Pill>
              <Pill tone={TONE[c.status] || "gold"}>{pretty(c.status)}</Pill>
              <span className="text-nex-silver/80 truncate flex-1">{c.notes || c.data?.label || ""}</span>
              <span className="text-[11px] text-nex-muted whitespace-nowrap">{fmtDateTime(c.occurred_at)}</span>
              {editing && <GhostButton data-testid={`checkin-edit-open-${c.id}`} onClick={() => setEdit({ id: c.id, status: c.status, notes: c.notes || "" })} className="!px-1.5" aria-label="Edit check-in"><Pencil className="h-3 w-3" /></GhostButton>}
              <GhostButton data-testid={`checkin-delete-${c.id}`} onClick={() => remove(c.id)} className="!px-1.5" aria-label="Delete check-in"><Trash2 className="h-3 w-3" /></GhostButton>
            </li>
          )))}
        </ul>
      )}
    </div>
  );
}
